import React, { useState, useRef } from 'react';

interface ValentineCardProps {
    onYesClick: () => void;
}

const ValentineCard: React.FC<ValentineCardProps> = ({ onYesClick }) => {
    const [noText, setNoText] = useState("No");
    const [scale, setScale] = useState(1);
    const [attempts, setAttempts] = useState(0);
    const [isMoved, setIsMoved] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const [buttonStyle, setButtonStyle] = useState<React.CSSProperties>({});

    const noTexts = [
        "No",
        "❤️ Think again 😉",
        "😢 Are you sure?",
        "💔 Don’t do this",
        "🥺 Please reconsider",
        "💕 Say yes?",
        "😭 My poor heart",
        "😉 Just say yes",
        "💖 One more chance",
        "😅 Really no?",
        "❤️ Choose love"
    ];

    const moveButton = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();

        // Define exact spots relative to the card container
        // Using percentages and calc to keep it "near" and "neat"
        const positions: React.CSSProperties[] = [
            { top: '0%', left: '0%', transform: 'translate(-50%, -50%)' }, // Top Left Corner
            { top: '0%', right: '0%', transform: 'translate(50%, -50%)' }, // Top Right Corner
            { bottom: '0%', left: '0%', transform: 'translate(-50%, 50%)' }, // Bottom Left Corner
            { bottom: '0%', right: '0%', transform: 'translate(50%, 50%)' }, // Bottom Right Corner
            { top: '50%', left: '0%', transform: 'translate(-80%, -50%)' }, // Left Middle
            { top: '50%', right: '0%', transform: 'translate(80%, -50%)' }, // Right Middle
            { top: '10%', left: '50%', transform: 'translate(-50%, -200%)' }, // Top Center
            { bottom: '0%', left: '50%', transform: 'translate(-50%, 150%)' }, // Bottom Center
        ];

        // Pick a random position
        const randomPos = positions[Math.floor(Math.random() * positions.length)];

        // Add movement state and randomness
        setAttempts(prev => prev + 1);
        setIsMoved(true);
        setNoText(noTexts[Math.min(attempts + 1, noTexts.length - 1)]);

        // Update style with absolute positioning and new scale/rotation
        const newRotation = Math.random() * 20 - 10;
        const newScale = Math.max(0.7, scale - 0.05);
        setScale(newScale);

        setButtonStyle({
            ...randomPos,
            position: 'absolute',
            transform: `${randomPos.transform} scale(${newScale}) rotate(${newRotation}deg)`,
            transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            zIndex: 50
        });
    };

    const handleYesClick = () => {
        onYesClick();
    };

    return (
        <div
            ref={cardRef}
            className="relative bg-white/95 backdrop-blur-xl rounded-[2rem] p-8 md:p-12 shadow-[0_15px_40px_rgba(255,182,193,0.3)] border border-pink-100 max-w-sm w-full text-center mx-4 transition-all duration-500"
        >
            <div className="text-5xl mb-4 animate-bounce">💝</div>
            <h1 className="text-3xl font-black text-pink-600 mb-6 font-serif leading-tight">
                Will you be my <br />
                <span className="italic">Valentine?</span> ❤️
            </h1>

            <div className="flex items-center justify-center gap-4 mt-8 min-h-[50px]">
                <button
                    onClick={attempts >= 4 ? handleYesClick : undefined}
                    disabled={attempts < 4}
                    className={`px-8 py-3 text-white text-lg font-bold rounded-full shadow-lg transition-all duration-300 transform z-20 ${attempts >= 4
                        ? "bg-pink-600 hover:bg-pink-700 hover:scale-105 active:scale-95 cursor-pointer animate-bounce"
                        : "bg-pink-400 opacity-50 cursor-not-allowed scale-95"
                        }`}
                >
                    {attempts >= 4 ? "YES! 💖" : "YES! 🔒"}
                </button>

                <button
                    onMouseEnter={moveButton}
                    onMouseOver={moveButton}
                    onClick={moveButton}
                    style={isMoved ? buttonStyle : {}}
                    className="px-8 py-3 bg-red-500 hover:bg-red-600 text-white text-lg font-bold rounded-full shadow-lg whitespace-nowrap transition-all animate-pulse"
                >
                    {noText}
                </button>
            </div>

            {attempts > 0 && (
                <div className="mt-6 text-pink-400 font-bold italic text-sm">
                    {attempts < 5 && "Wait, really? 🤔"}
                    {attempts >= 5 && attempts < 10 && "Getting tired yet? 😴"}
                    {attempts >= 10 && "Just say YES! 🥺"}
                </div>
            )}
        </div>
    );
};

export default ValentineCard;
